import "../styles/StatusMessage.css";

import {
  HighContrastSelector,
  IStyle,
  ITheme,
  Link,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Text,
  useTheme,
} from "@fluentui/react";
import React from "react";

import { useGetServiceStatusQuery } from "../api";
import { TdsServiceStatus } from "../constants/types";
import { linkColor } from "../styles/themes";
import { isDarkModeTheme } from "../utils/TypescriptUtils";

type ServiceStatusCodeMap = {
  [key in TdsServiceStatus]: MessageBarType;
};

const serviceStatusCodeMapping: ServiceStatusCodeMap = {
  [TdsServiceStatus.Normal]: MessageBarType.info,
  [TdsServiceStatus.Warning]: MessageBarType.severeWarning,
  [TdsServiceStatus.Error]: MessageBarType.error,
};

const getStyles = (theme: ITheme) => {
  const fontColor = isDarkModeTheme(theme) ? theme.palette.neutralLight : theme.palette.neutralPrimary;

  return mergeStyleSets<{ statusTitle: IStyle; statusText: IStyle }>({
    statusTitle: {
      color: fontColor,
      fontWeight: "bolder",
      [HighContrastSelector]: {
        color: "WindowText",
        forcedColorAdjust: "auto",
      },
    },
    statusText: {
      color: fontColor,
      "a:last-child": {
        paddingLeft: 0 + "!important",
      },
      [HighContrastSelector]: {
        color: "WindowText",
        forcedColorAdjust: "auto",
      },
    },
  });
};

const ServiceStatusMessage: React.FC = () => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const ICM_TOKEN_REGEX = /ICM[:]{1}\d+/gi;

  const { data: serviceStatus, error } = useGetServiceStatusQuery();

  // eslint-disable-next-line no-console
  if (error) console.error(error);

  /**
   * Generes the ICM link for the service status message.
   * @param icmId - The ICM ID
   * @returns - The ICM link
   */
  const generateIcmLink = (icmId: string) => (
    <Link
      key={icmId}
      underline
      href={`https://portal.microsofticm.com/imp/v3/incidents/details/${icmId}/home`}
      target="_blank"
      styles={{
        root: isDarkModeTheme(theme) && {
          color: `${linkColor} !important`,
        },
      }}
    >
      IcM {icmId}
    </Link>
  );

  /**
   * When rendering the message, extract any discovered "ICM:<number>" text in the status
   * message into a link that points to the actual ICM website with the text "ICM Incident <number>"
   *
   * @param message the status message text received from the API
   * @returns either just the message as a string or an array of string and elements,
   * with the elements being auto-generated ICM Links.
   */
  const renderStatusMessage = (message: string): string | Array<JSX.Element | string> => {
    const splitted = message.split(ICM_TOKEN_REGEX);
    const matches = message.match(ICM_TOKEN_REGEX);

    if (matches === null) return message;

    const messageParts: Array<JSX.Element | string> = [];

    splitted.forEach((splitMessagePart, i) => {
      messageParts.push(splitMessagePart);

      const correspondingMatch = matches[i];
      if (correspondingMatch) {
        const icmId = correspondingMatch.substring("ICM:".length);
        messageParts.push(generateIcmLink(icmId));
      }
    });

    return messageParts;
  };

  return (
    <>
      {serviceStatus && serviceStatus.Message !== "" ? (
        <MessageBar messageBarType={serviceStatusCodeMapping[serviceStatus.StatusCode]}>
          <Text className={styles.statusTitle}>TDS Service Status: </Text>
          <Text className={styles.statusText}>{renderStatusMessage(serviceStatus.Message)}</Text>
        </MessageBar>
      ) : null}
    </>
  );
};

export default ServiceStatusMessage;
